/**
 * CTA click tracking (Appel / WhatsApp).
 * -------------------------------------
 * Single entry point used by every call & WhatsApp button of the public site.
 *
 * Two sinks, both fire-and-forget and both PII-free:
 *  1. `@/lib/analytics` (PostHog + Sentry breadcrumb) for funnels.
 *  2. `public.cta_clicks` in Lovable Cloud so the admin dashboard can display
 *     counts without depending on an external analytics provider.
 *
 * Payload is strictly enumerated: channel, source slug, route path and UI
 * language. Never a phone number, never free-form text.
 */

import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';

export type CtaChannel = 'call' | 'whatsapp';

/** Where the button lives. Keep slugs short, stable and opaque. */
export type CtaSource =
  | 'contact_page'
  | 'footer'
  | 'mobile_action_bar'
  | 'quote_request'
  | 'afamekin2_hero'
  | 'afamekin2_contact_block'
  | 'afamekin2_compliance'
  | 'actualites_page'
  | 'careers_page'
  | 'references_page'
  | 'home_hero';

interface CtaContext {
  /** Route path (already sanitized: no query string, no ids). */
  path?: string;
  language?: 'fr' | 'en';
}

/** Normalizes a route so no id / query string is ever persisted. */
const safePath = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  const path = raw.split('?')[0].split('#')[0];
  if (path.length > 128) return path.slice(0, 128);
  return path;
};

/**
 * Records a click on a call or WhatsApp CTA. Never throws and never blocks
 * navigation — the `tel:`/`wa.me` link keeps its native behaviour.
 */
export function trackCtaClick(
  channel: CtaChannel,
  source: CtaSource,
  ctx: CtaContext = {},
): void {
  const path = safePath(ctx.path);

  track(channel === 'call' ? 'phone_click' : 'whatsapp_click', {
    source,
    path,
    language: ctx.language,
  });

  try {
    // `cta_clicks` is an analytics-only sink; the generated types are refreshed
    // asynchronously, so we insert through an untyped view of the client.
    const db = supabase as unknown as {
      from: (t: string) => {
        insert: (v: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
      };
    };
    void Promise.resolve(
      db.from('cta_clicks').insert({
        channel,
        source,
        path: path ?? null,
        language: ctx.language ?? null,
      }),
    ).then(({ error }) => {
      if (error && import.meta.env.DEV) console.warn('[cta] persist failed', error.message);
    });
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[cta] persist threw', err);
  }
}
